/**
 * deleteEvent — RPC contract for the deleteEvent callable, the only path that
 * destroys an event. `event.entity.ts` marks `delete`
 * `.requiresHandler('deleteEvent')`. A Firestore rule can gate on the
 * caller's own attributes and the event doc's own fields, but the fact that
 * decides this delete — whether any order line, ticket or still-live checkout
 * anywhere beneath the event names it — is a query no rule can run, so the
 * authorization is the callable itself.
 *
 *
 * Fields justified:
 *   eventId — the event to delete; the path is
 *             workspaces/{orgId}/events/{eventId}.
 * Excluded:
 *   a confirmation flag — acknowledgement is what the form renders, and a
 *           flag the caller sets to true guards nothing the caller could not
 *           set anyway; the census inside the transaction is the guard.
 *
 * Output: a discriminated union on `outcome`, because a refusal is the
 * answer the operator asked for — the counts standing in the way, rendered
 * with the remedy — not an exception. An unauthenticated or unpermitted
 * caller still throws `HttpsError`; that is not an answer, it is a closed
 * door.
 *
 * Output fields justified:
 *   deletedTicketTypeCount — the types the cascade removed with the event,
 *                            each with its own capacity ledger; the event's
 *                            catalog subtree has no other owner once the
 *                            event is gone.
 *   deletedCheckoutCount   — the terminal checkout records the cascade
 *                            removed; a non-terminal one refuses the delete
 *                            instead, so this counts only settled history.
 *   dependents             — the census, on refusal only.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { z } from "zod";
import { DependentsSchema } from "./dependents.const.ts";
import { Id } from "./workspace.entity.ts";

export const DeleteEventInput = z
	.object({ orgId: Id, eventId: Id.describe("the event to delete") })
	.strict();
export type DeleteEventInput = z.infer<typeof DeleteEventInput>;

export const DeleteEventOutput = z.discriminatedUnion("outcome", [
	z
		.object({
			outcome: z.literal("deleted"),
			deletedTicketTypeCount: z
				.number()
				.int()
				.min(0)
				.describe(
					"how many ticket types the cascade destroyed with the event, each together with its capacity ledger",
				),
			deletedCheckoutCount: z
				.number()
				.int()
				.min(0)
				.describe(
					"how many terminal checkout records the cascade destroyed with the event",
				),
		})
		.strict(),
	z
		.object({
			outcome: z.literal("refused"),
			dependents: DependentsSchema.describe(
				"what still stands in the way of the delete, in counts — the refusal's whole substance",
			),
		})
		.strict(),
]);
export type DeleteEventOutput = z.infer<typeof DeleteEventOutput>;
